import React, { useState, useEffect, useMemo, useRef, useCallback, startTransition } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import entryGuideService from '../../services/entryGuideService';
import Toast from '../../components/Common/Toast';
import FormLoadingSpinner from '../../components/Common/FormLoadingSpinner';
import { formatDateToLima, formatTimeToLima, normalizeTimeForApi } from '../../utils/dateUtils';
import {
  STOCK_QUALITY_COLUMNS,
  countedSimpleToByCondition,
  convertRowsCountMode,
  mergeAdjustmentRowsFromBulk,
  buildAdjustmentSubmitRows,
  formatAdjustmentSummaryMessage,
  adjustmentSummaryToastType,
  isMissingCatalogProduct,
} from '../../utils/stockByConditionDisplay';
import {
  exportAdjustmentGuideExcel,
  parseAdjustmentCountedImport,
} from '../../utils/adjustmentGuideExcel';
import AdjustmentGuideItemsPanel from './AdjustmentGuideItemsPanel';

const num = (v) => {
  const x = parseFloat(v);
  return Number.isFinite(x) ? x : 0;
};

const CreateAdjustmentGuide = () => {
  const navigate = useNavigate();
  const { warehouseId } = useParams();
  const wid = parseInt(warehouseId, 10);

  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [loading, setLoading] = useState(true);
  const [stockLoading, setStockLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [suppliers, setSuppliers] = useState([]);

  const today = formatDateToLima(new Date());
  const nowTime = formatTimeToLima(new Date()) || '12:00';

  const [supplierId, setSupplierId] = useState('');
  const [issueDate, setIssueDate] = useState(today);
  const [entryDate, setEntryDate] = useState(today);
  const [entryTime, setEntryTime] = useState(nowTime);
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [adjustmentMode, setAdjustmentMode] = useState('simple');
  const [stockLoaded, setStockLoaded] = useState(false);
  const [excelBusy, setExcelBusy] = useState(false);
  const importExcelRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await entryGuideService.getFormData();
        if (cancelled) return;
        if (res.success && res.data) {
          if (res.data.suppliers) {
            setSuppliers(res.data.suppliers);
          }
        }
      } catch (e) {
        console.error(e);
        setToast({ isOpen: true, message: 'Error al cargar datos del formulario', type: 'error' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [wid]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.name && r.name.toLowerCase().includes(q)) ||
        (r.sku && String(r.sku).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const updateRow = useCallback((productId, field, value) => {
    setRows((prev) =>
      prev.map((r) => (r.product_id === productId ? { ...r, [field]: value } : r))
    );
  }, []);

  const updateRowCountedCond = useCallback((productId, key, value) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.product_id !== productId) return r;
        const base = r.countedByCondition || {};
        return { ...r, countedByCondition: { ...base, [key]: value } };
      })
    );
  }, []);

  const updateRowCountedSimple = useCallback((productId, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r.product_id === productId
          ? { ...r, countedByCondition: countedSimpleToByCondition(value) }
          : r
      )
    );
  }, []);

  const handleAdjustmentModeChange = useCallback((next) => {
    setAdjustmentMode(next);
    startTransition(() => {
      setRows((prev) => {
        let updated = prev;
        if (next === 'quality' || next === 'simple') {
          updated = convertRowsCountMode(prev);
        } else if (next === 'cost_only') {
          updated = prev.map((r) => {
            if (r.stockAtEntry === null || r.stockAtEntry === undefined) return r;
            if (Math.abs(num(r.stockAtEntry)) < 0.0001) return r;
            const costStr =
              r.targetCostManual !== null && r.targetCostManual !== undefined && String(r.targetCostManual).trim() !== ''
                ? String(r.targetCostManual)
                : String(num(r.averageCost));
            return { ...r, targetCostManual: costStr };
          });
        }
        return updated;
      });
    });
  }, []);

  const handleLoadStock = async () => {
    if (!entryDate) {
      setToast({ isOpen: true, message: 'Indique la fecha de ingreso', type: 'warning' });
      return;
    }
    const timeForApi = normalizeTimeForApi(entryTime);
    setStockLoading(true);
    try {
      const res = await entryGuideService.getProductStockAndCostBulk(
        wid,
        null,
        entryDate,
        timeForApi,
        true
      );
      if (!res.success) {
        setToast({ isOpen: true, message: res.message || 'Error al cargar stock', type: 'error' });
        return;
      }
      const data = res.data || {};
      const productCount = Object.keys(data).length;
      if (productCount === 0) {
        setToast({
          isOpen: true,
          message: 'No hay productos activos visibles para este almacén.',
          type: 'warning',
        });
        return;
      }
      startTransition(() => {
        setRows((prev) =>
          mergeAdjustmentRowsFromBulk(prev, data, { adjustmentMode })
        );
      });
      setStockLoaded(true);
      setToast({
        isOpen: true,
        message:
          adjustmentMode === 'cost_only'
            ? `Stock cargado (${productCount} productos) según ingreso ${entryDate} ${timeForApi}. Indique el nuevo costo promedio donde difiera del actual.`
            : `Stock cargado (${productCount} productos) según ingreso ${entryDate} ${timeForApi}. Ajuste el conteo físico donde difiera.`,
        type: 'success',
      });
    } catch (e) {
      console.error(e);
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al cargar stock',
        type: 'error',
      });
    } finally {
      setStockLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (rows.length === 0) return;
    setExcelBusy(true);
    try {
      await exportAdjustmentGuideExcel(rows, `Ajuste_inventario_almacen_${wid}`, {
        simpleMode: adjustmentMode === 'simple',
      });
      setToast({
        isOpen: true,
        message: adjustmentMode === 'quality'
          ? `Excel generado (${rows.length} productos). Edite solo columnas Conteo_* y reimporte si lo desea.`
          : `Excel generado (${rows.length} productos). Edite la columna Conteo_total y reimporte si lo desea.`,
        type: 'success',
      });
    } catch (e) {
      console.error(e);
      setToast({ isOpen: true, message: 'No se pudo generar el Excel.', type: 'error' });
    } finally {
      setExcelBusy(false);
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setExcelBusy(true);
    try {
      const buf = await file.arrayBuffer();
      const { updates } = await parseAdjustmentCountedImport(buf, { simpleMode: adjustmentMode === 'simple' });
      if (updates.length === 0) {
        setToast({
          isOpen: true,
          message: adjustmentMode === 'quality'
            ? 'No hay filas con conteo en el archivo (rellene columnas Conteo_*).'
            : 'No hay filas con conteo en el archivo (rellene la columna Conteo_total).',
          type: 'warning',
        });
        return;
      }
      const byId = new Map(updates.map((u) => [u.productId, u]));
      setRows((prev) =>
        prev.map((r) => {
          const imported = byId.get(r.product_id);
          if (!imported) return r;
          const patch = imported.patch || {};
          const base = r.countedByCondition
            ? { ...r.countedByCondition }
            : Object.fromEntries(STOCK_QUALITY_COLUMNS.map(({ key }) => [key, '']));
          for (const [k, v] of Object.entries(patch)) {
            base[k] = v;
          }
          const editableCost =
            r.stockAtEntry !== null &&
            r.stockAtEntry !== undefined &&
            Math.abs(num(r.stockAtEntry)) < 0.0001 &&
            imported.averageCostManual !== null;
          return {
            ...r,
            countedByCondition: base,
            averageCostManual: editableCost ? imported.averageCostManual : r.averageCostManual,
          };
        })
      );
      setToast({
        isOpen: true,
        message: `Conteo físico importado. El costo se aplicó solo en productos editables (stock 0).`,
        type: 'success',
      });
    } catch (err) {
      console.error(err);
      setToast({
        isOpen: true,
        message: err?.message || 'No se pudo leer el Excel. Use un archivo exportado desde esta pantalla.',
        type: 'error',
      });
    } finally {
      setExcelBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!supplierId) {
      setToast({ isOpen: true, message: 'Seleccione la entidad (proveedor)', type: 'warning' });
      return;
    }
    if (!stockLoaded) {
      setToast({
        isOpen: true,
        message: 'Primero pulse Cargar stock con la fecha y hora de ingreso.',
        type: 'warning',
      });
      return;
    }

    const items = buildAdjustmentSubmitRows(rows, adjustmentMode);

    if (items.length === 0) {
      setToast({
        isOpen: true,
        message:
          adjustmentMode === 'cost_only'
            ? 'No hay productos con diferencia de costo para ajustar.'
            : 'No hay productos con diferencia de conteo para ajustar.',
        type: 'warning',
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        guide_type: 'ajuste',
        adjustment_mode: adjustmentMode,
        warehouse_id: wid,
        supplier_id: parseInt(supplierId, 10),
        issue_date: issueDate,
        entry_date: entryDate,
        entry_time: normalizeTimeForApi(entryTime, { endOfDayIfEmpty: false }),
        notes: notes || null,
        items,
      };
      const debug68 = items.filter((it) => Number(it.product_id) === 68);
      if (debug68.length > 0) {
        console.log('[AJUSTE-DEBUG-68][FRONT][CREATE][payload]', {
          warehouse_id: wid,
          entry_date: entryDate,
          entry_time: entryTime || null,
          items_68: debug68,
        });
      }
      const res = await entryGuideService.createEntryGuide(payload);
      if (res.success) {
        setToast({
          isOpen: true,
          message: formatAdjustmentSummaryMessage(res),
          type: adjustmentSummaryToastType(res),
        });
        setTimeout(() => navigate(`/warehouses/${warehouseId}/documents`), 1200);
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo guardar', type: 'error' });
      }
    } catch (err) {
      const errObj = err.response?.data?.errors;
      let detail = err.response?.data?.message;
      if (errObj && typeof errObj === 'object') {
        const firstKey = Object.keys(errObj)[0];
        const firstVal = firstKey ? errObj[firstKey] : null;
        const firstMsg = Array.isArray(firstVal) ? firstVal[0] : firstVal;
        if (firstMsg && detail === 'Error de validación') {
          detail = firstMsg;
        }
      }
      const msg =
        err.response?.data?.conversion_errors?.join('; ') ||
        formatAdjustmentSummaryMessage(err.response?.data) ||
        detail ||
        'Error al guardar';
      setToast({ isOpen: true, message: msg, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-[110rem] mx-auto px-1">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-800">Nueva guía de ajuste de inventario</h1>
          </div>
          <FormLoadingSpinner message="Cargando formulario…" hint="Preparando productos y proveedores del almacén" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-[110rem] mx-auto px-1">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Nueva guía de ajuste de inventario</h1>
            <p className="text-gray-600 mt-1">
            Elija la entidad y la fecha/hora de ingreso; use <strong>Cargar stock</strong> para traer el inventario del
            sistema a ese momento (no precarga todo el catálogo hasta pulsar ese botón). Puede contar{' '}
            <strong>por calidad</strong>, usar <strong>ajuste simple</strong> (solo total) o{' '}
            <strong>solo costos</strong> (ajuste de costo promedio sin mover cantidades).
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          <div className="bg-white rounded-lg shadow p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Entidad (proveedor)</label>
              <select
                className="w-full border rounded-md px-3 py-2 bg-white"
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                required
              >
                <option value="">— Seleccionar —</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha emisión</label>
              <input
                type="date"
                className="w-full border rounded-md px-3 py-2"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha ingreso</label>
              <input
                type="date"
                className="w-full border rounded-md px-3 py-2"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="block text-sm font-medium text-gray-700">Hora ingreso</label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="time"
                  className="border rounded-md px-3 py-2 flex-1 min-w-[120px]"
                  value={entryTime}
                  onChange={(e) => setEntryTime(e.target.value)}
                />
                <button
                  type="button"
                  onClick={handleLoadStock}
                  disabled={stockLoading}
                  className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 whitespace-nowrap"
                >
                  {stockLoading ? 'Cargando…' : 'Cargar stock'}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                El stock se calcula con movimientos anteriores a la fecha y hora de ingreso (si no indica hora, se usa
                23:59).
              </p>
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
              <textarea
                className="w-full border rounded-md px-3 py-2"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          <AdjustmentGuideItemsPanel
            search={search}
            onSearchChange={setSearch}
            filteredRows={filteredRows}
            totalRows={rows.length}
            countLabel="total"
            adjustmentMode={adjustmentMode}
            onAdjustmentModeChange={handleAdjustmentModeChange}
            excelBusy={excelBusy}
            onExportExcel={handleExportExcel}
            importExcelRef={importExcelRef}
            onImportExcel={handleImportExcel}
            exportDisabled={rows.length === 0}
            onUpdateRow={updateRow}
            onUpdateRowCountedCond={updateRowCountedCond}
            onUpdateRowCountedSimple={updateRowCountedSimple}
            deferFullList
            stockLoaded={stockLoaded}
          />

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50"
            >
              {submitting ? 'Guardando…' : 'Registrar guía de ajuste'}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/warehouses/${warehouseId}/documents`)}
              className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              Cancelar
            </button>
          </div>
        </form>

        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, isOpen: false })}
        />
      </div>
    </div>
  );
};

export default CreateAdjustmentGuide;
